import { useState } from "react";
import { ExternalLink, Share2 } from "lucide-react";
import { NETWORKS } from "@/data/networks";

const HUB = {
  key: "hub", name: "Hub", path: "/",
  title: "HearthVault on Facebook, YouTube, Instagram & LinkedIn",
  description: "Follow HearthVault, the family document safe, on Facebook, YouTube, Instagram and LinkedIn — checklists, walkthroughs and advisor notes for the family folder.",
};
const PAGES = [HUB, ...NETWORKS.map((n) => ({ key: n.key, name: n.name, path: `/${n.key}`, title: n.title, description: n.description }))];
const STYLES = ["Facebook", "LinkedIn", "X / iMessage"];
const clip = (s, n) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);

const FacebookCard = ({ p, host, img }) => (
  <div className="w-full max-w-[500px] rounded-lg border border-[#dadde1] bg-white overflow-hidden font-sans" data-testid={`share-card-facebook-${p.key}`}>
    <img src={img} alt="" className="w-full aspect-[1.91/1] object-cover" />
    <div className="bg-[#f0f2f5] px-3 py-2.5">
      <div className="text-[12px] uppercase text-[#65676b] truncate">{host}</div>
      <div className="mt-0.5 text-[16px] font-semibold leading-5 text-[#050505]">{clip(p.title, 88)}</div>
      <div className="mt-0.5 text-[14px] leading-5 text-[#65676b] line-clamp-1">{p.description}</div>
    </div>
  </div>
);

const LinkedInCard = ({ p, host, img }) => (
  <div className="w-full max-w-[500px] rounded-lg border border-[#e0dfdc] bg-white overflow-hidden font-sans" data-testid={`share-card-linkedin-${p.key}`}>
    <img src={img} alt="" className="w-full aspect-[1.91/1] object-cover" />
    <div className="bg-[#eef3f8] px-3 py-2.5">
      <div className="text-[14px] font-semibold leading-5 text-[rgba(0,0,0,0.9)]">{clip(p.title, 120)}</div>
      <div className="mt-0.5 text-[12px] text-[rgba(0,0,0,0.6)]">{host} · 2 min read</div>
    </div>
  </div>
);

const CompactCard = ({ p, host, img }) => (
  <div className="w-full max-w-[500px] rounded-2xl border border-[#cfd9de] bg-white overflow-hidden font-sans" data-testid={`share-card-x-${p.key}`}>
    <div className="relative">
      <img src={img} alt="" className="w-full aspect-[1.91/1] object-cover" />
      <span className="absolute left-3 bottom-3 rounded bg-black/70 px-1.5 py-0.5 text-[13px] text-white">{clip(p.title, 60)}</span>
    </div>
    <div className="px-3 py-2 text-[13px] text-[#536471]">From {host}</div>
  </div>
);

export const SharePreview = () => {
  const [style, setStyle] = useState(0);
  const origin = window.location.origin;
  const host = origin.replace(/^https?:\/\//, "");
  const Card = [FacebookCard, LinkedInCard, CompactCard][style];

  return (
    <section className="mt-12 rounded-[2rem] border border-[#E8DDD3] bg-white p-6 sm:p-8" data-testid="stats-share-preview">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="h-9 w-9 rounded-xl grid place-items-center bg-[#1F1A17] text-[#FBF8F4]"><Share2 className="h-4 w-4" /></span>
          <div>
            <h2 className="font-serif text-2xl tracking-tight text-[#1F1A17]">Share preview</h2>
            <p className="text-xs text-[#685F58]">How each page's link unfurls when someone pastes it. Rendered from the live title, description and share image.</p>
          </div>
        </div>
        <div className="flex gap-2" role="tablist">
          {STYLES.map((s, i) => (
            <button key={s} role="tab" aria-selected={style === i} onClick={() => setStyle(i)} data-testid={`share-style-${i}`}
              className={`h-9 px-4 rounded-full text-xs font-medium transition-colors duration-300 ${style === i ? "bg-[#1F1A17] text-[#FBF8F4]" : "border border-[#E8DDD3] text-[#685F58] hover:text-[#1F1A17]"}`}>{s}</button>
          ))}
        </div>
      </div>

      <div className="mt-6 grid md:grid-cols-2 xl:grid-cols-3 gap-6">
        {PAGES.map((p) => (
          <div key={p.key} data-testid={`share-preview-${p.key}`}>
            <div className="mb-2 flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#685F58]">{p.name} · {p.path}</span>
              <a href={`/og/${p.key}.jpg`} target="_blank" rel="noreferrer" className="text-[11px] text-[#685F58] hover:text-[#1F1A17] underline underline-offset-2" data-testid={`share-image-link-${p.key}`}>image 1200×630</a>
            </div>
            <Card p={p} host={host} img={`/og/${p.key}.jpg`} />
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-2xl bg-[#FBF8F4] border border-[#E8DDD3] p-4 text-xs leading-relaxed text-[#685F58]" data-testid="share-preview-note">
        Crawlers only read these tags on the <strong className="text-[#1F1A17]">deployed</strong> site (with bot prerendering on). After publishing, confirm with{" "}
        <a className="underline underline-offset-2 hover:text-[#1F1A17]" href="https://developers.facebook.com/tools/debug/" target="_blank" rel="noreferrer">Facebook Sharing Debugger <ExternalLink className="inline h-3 w-3" /></a>{" "}
        and{" "}
        <a className="underline underline-offset-2 hover:text-[#1F1A17]" href="https://www.linkedin.com/post-inspector/" target="_blank" rel="noreferrer">LinkedIn Post Inspector <ExternalLink className="inline h-3 w-3" /></a>
        — both also clear a stale cache after you change the image or copy.
      </div>
    </section>
  );
};
